import React, { useState } from "react";
import { message } from "@centrate-io/barn";
import { useApolloClient } from "@apollo/client";
import { IconLoader2, IconShieldLock } from "@tabler/icons-react";
import { cpAdminLogin } from "_graphql/mutations";
import { usePortalSettings } from "_contexts/usePortalSettings";
import "./AdminLogin.scss";

// Background images
import FieldsImage from "_assets/images/customer-portal/fields.jpg";
import HillsImage from "_assets/images/customer-portal/hills.jpg";
import MountainsImage from "_assets/images/customer-portal/mountains.jpg";
import PoppiesImage from "_assets/images/customer-portal/poppies.jpg";
import SunsetImage from "_assets/images/customer-portal/sunset.jpg";

const backgroundImages = {
  fields: FieldsImage,
  hills: HillsImage,
  mountains: MountainsImage,
  poppies: PoppiesImage,
  sunset: SunsetImage,
};

function AdminLogin({ onSuccess }) {
  const client = useApolloClient();
  const { settings } = usePortalSettings();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Get company settings
  const logoUrl = settings?.logo_url;
  const companyName = settings?.company?.name || "Paypath";
  const backgroundImage = settings?.background_image || "poppies";

  // Get background image URL
  const bgImageUrl =
    backgroundImages[backgroundImage] || backgroundImages.poppies;

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) {
      message.error("Please enter your email and password");
      return;
    }

    setLoading(true);

    try {
      const result = await client.mutate({
        mutation: cpAdminLogin,
        variables: {
          email: email.trim().toLowerCase(),
          password: password,
        },
      });

      const login = result?.data?.cpAdminLogin;

      if (!login?.success) {
        message.error(login?.error || "Login failed");
        setLoading(false);
        return;
      }

      // Pass to two-factor step
      onSuccess({
        userId: login.user_id,
        email: login.email,
        phone: login.phone,
        name: login.name,
      });
    } catch (err) {
      console.error("AdminLogin error:", err);
      message.error("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && email.trim() && password.trim()) {
      handleLogin();
    }
  };

  const isValid = email.trim() && password.trim();

  return (
    <div className="admin-login-container">
      {/* Background with blur */}
      <div
        className="auth-background"
        style={{ backgroundImage: `url(${bgImageUrl})` }}
      />

      {/* Modal */}
      <div className="auth-modal">
        <div className="auth-content">
          {/* Brand Icon */}
          {logoUrl && (
            <div className="brand-icon">
              <img src={logoUrl} alt={companyName} />
            </div>
          )}

          {/* Admin Badge */}
          <div className="admin-badge">
            <IconShieldLock size={16} />
            <span>Admin Access</span>
          </div>

          {/* Welcome Text */}
          <div className="welcome-section">
            <h1 className="welcome-title">Admin Sign In</h1>
            <p className="welcome-subtitle">
              Super administrators can sign in here to access customer accounts.
            </p>
          </div>

          {/* Input Section */}
          <div className="input-section">
            <div className="input-group">
              <label className="input-label">Email</label>
              <div className="input-container">
                <input
                  type="email"
                  placeholder="Enter your email..."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Password</label>
              <div className="input-container">
                <input
                  type="password"
                  placeholder="Enter your password..."
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>
            </div>

            <button
              className={`continue-button ${isValid && !loading ? "active" : ""}`}
              onClick={handleLogin}
              disabled={!isValid || loading}
            >
              {loading ? (
                <IconLoader2 className="loading-icon" size={20} />
              ) : (
                "Sign In"
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="auth-footer">
          <a href="/" className="back-to-customer">
            ← Back to Customer Portal
          </a>
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;

